/**
 * Analytics identity module.
 *
 * Produces the stable envelope fields required by every analytics event
 * (metrics-plan §0 — "Standard properties"):
 *
 *   anon_id      UUID v4, persisted to localStorage `snip_anon_id`, created once.
 *   session_id   UUID v4, persisted to sessionStorage `bp_session_id` (new each tab).
 *   is_returning true if `snip_anon_id` already existed in localStorage before this load.
 *   viewport     Breakpoint derived from window.innerWidth:
 *                  desktop  ≥ 1024px
 *                  tablet   768–1023px
 *                  mobile   < 768px
 *   app_version  Injected by Vite define (VITE_APP_VERSION) or falls back to
 *                package.json `version` via import.meta.env.VITE_APP_VERSION.
 *                See vite.config.ts: `define: { __APP_VERSION__: JSON.stringify(version) }`.
 *                Falls back to '0.0.0' if neither define nor env var is present.
 *   dnt          true if navigator.doNotTrack === '1'.
 *
 * All values are read once per page load and cached in the module closure so
 * repeated calls are synchronous and pure (no re-reading storage mid-session).
 */

import type { AnalyticsEvent } from 'briefpane/contracts';

const ANON_ID_KEY = 'snip_anon_id';
const SESSION_ID_KEY = 'bp_session_id';

/** Generate a UUID v4 using crypto.randomUUID() (supported in all modern browsers). */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID (test environments).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Read or create the persistent anon_id. Returns {id, wasNew}. */
function resolveAnonId(): { readonly id: string; readonly wasNew: boolean } {
  try {
    const existing = localStorage.getItem(ANON_ID_KEY);
    if (existing !== null) {
      return { id: existing, wasNew: false };
    }
    const fresh = generateUUID();
    localStorage.setItem(ANON_ID_KEY, fresh);
    return { id: fresh, wasNew: true };
  } catch {
    // Storage unavailable (private mode, quota full, etc.) — generate ephemeral id.
    return { id: generateUUID(), wasNew: true };
  }
}

/** Read or create the per-session session_id. */
function resolveSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing !== null) return existing;
    const fresh = generateUUID();
    sessionStorage.setItem(SESSION_ID_KEY, fresh);
    return fresh;
  } catch {
    return generateUUID();
  }
}

/** Derive the viewport breakpoint from window width at load time. */
function resolveViewport(): 'desktop' | 'tablet' | 'mobile' {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1280;
  if (width >= 1024) return 'desktop';
  if (width >= 768) return 'tablet';
  return 'mobile';
}

/** App version from Vite define or env var, with safe fallback. */
function resolveAppVersion(): string {
  // __APP_VERSION__ is injected by the Vite `define` config.
  // VITE_APP_VERSION is an env-var override (e.g. in CI).
  try {
    if (typeof __APP_VERSION__ !== 'undefined') return __APP_VERSION__;
  } catch { /* not defined — fall through */ }
  try {
    if (import.meta.env.VITE_APP_VERSION) return import.meta.env.VITE_APP_VERSION as string;
  } catch { /* not in Vite context — fall through */ }
  return '0.0.0';
}

/** Check Do Not Track header (fail-closed on privacy — §0 rule 5). */
function resolveDnt(): boolean {
  return typeof navigator !== 'undefined' && navigator.doNotTrack === '1';
}

// --- Module-level singletons (read once per page load) ---

const { id: _anonId, wasNew: _wasNew } = resolveAnonId();
const _sessionId = resolveSessionId();
const _isReturning = !_wasNew;
const _viewport = resolveViewport();
const _appVersion = resolveAppVersion();
const _dnt = resolveDnt();

/** The resolved anonymous identity for this page load. */
export interface Identity {
  readonly anon_id: string;
  readonly session_id: string;
  /** true if the anon_id existed before this load (i.e. not a first-ever visit). */
  readonly is_returning: boolean;
  readonly viewport: 'desktop' | 'tablet' | 'mobile';
  readonly app_version: string;
  readonly dnt: boolean;
}

/** Returns the singleton identity resolved at module load time. */
export function getIdentity(): Identity {
  return {
    anon_id: _anonId,
    session_id: _sessionId,
    is_returning: _isReturning,
    viewport: _viewport,
    app_version: _appVersion,
    dnt: _dnt,
  };
}

/**
 * The analytics surface for this build. Defaults to 'web'.
 *
 * The extension build sets VITE_SURFACE=ext_newtab in .env.extension so that
 * events from the new-tab page are correctly attributed. The web build leaves
 * VITE_SURFACE unset → 'web', unchanged from today.
 *
 * 'ext_newtab' is a defined value in the SURFACES enum (src/contracts/analytics.ts).
 * Do NOT change the contract to accommodate this — VITE_SURFACE must only ever be
 * set to a value already in that enum; adding new values is a contract amendment.
 */
const ANALYTICS_SURFACE: 'web' | 'ext_newtab' | 'tv' = (() => {
  try {
    const v = import.meta.env.VITE_SURFACE as string | undefined;
    if (v === 'ext_newtab' || v === 'tv') return v;
  } catch { /* not in Vite context */ }
  return 'web';
})();

/**
 * Build the standard envelope fields for an analytics event.
 * Spread this into every event payload:
 *   `{ event: 'tile_added', ...buildEnvelope(), tile_type: '…', … }`
 *
 * The `surface` field is set by the VITE_SURFACE build define: 'web' for the
 * SPA (default), 'ext_newtab' for the extension build. Set in .env.extension;
 * see scripts/build-extension.mjs.
 */
export function buildEnvelope(): Omit<AnalyticsEvent, 'event'> {
  const id = getIdentity();
  return {
    anon_id: id.anon_id,
    session_id: id.session_id,
    ts_client: new Date().toISOString(),
    surface: ANALYTICS_SURFACE,
    app_version: id.app_version,
    is_returning: id.is_returning,
    viewport: id.viewport,
    dnt: id.dnt,
  };
}

// Global declaration for Vite define injection.
declare const __APP_VERSION__: string;
